import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  BookOpen,
  Bot,
  Check,
  ChevronLeft,
  Clipboard,
  DoorOpen,
  Eye,
  EyeOff,
  Info,
  KeyRound,
  LogOut,
  Mic2,
  RotateCcw,
  Send,
  Share2,
  Sparkles,
  UserMinus,
  Users,
  Wifi,
  WifiOff,
  X
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ACTIONS, ROLES } from "./gameData";
import { anchorRef, TABLE_CENTER } from "./fxAnchors";
import { apiUrl, ConnectError, GameSocket, type AckResult } from "./gameSocket";
import { createId } from "./id";
import type { ActionType, GameState, Influence, Player, Role, Session } from "./types";
import { ActionDock } from "./components/ActionDock";
import { AvatarCapture } from "./components/AvatarCapture";
import { DecisionPanel, InterferencePanel } from "./components/DecisionPanel";
import { TableFx } from "./components/Flourishes";
import { MyHand } from "./components/MyHand";
import { PlayerRail } from "./components/PlayerRail";
import { VoiceLinesSheet } from "./components/VoiceLinesSheet";
import { Wordmark } from "./components/Wordmark";
import {
  playVoiceLine,
  preloadVoiceLines,
  voiceLineForAction,
  voiceLineForBlock
} from "./voiceLines";
import "./appExtras.css";

const SESSION_KEY = "veiled-court-session";
const SITE_KEY = "veiled-court-site-password";
const CONFETTI_COLORS = ["#f5b82e", "#ff5d50", "#2d8cff", "#b9e532", "#a97af0"];
const STALE_DECISION_ERRORS = new Set([
  "There is nothing to pass.",
  "There is no response window.",
  "There is no role claim to call out.",
  "No Influence is being lost.",
  "No exchange is in progress."
]);

function App() {
  const [socket] = useState<GameSocket>(() => new GameSocket());
  const [connected, setConnected] = useState(false);
  const [sitePasswordRequired, setSitePasswordRequired] = useState<boolean | null>(null);
  const [siteUnlocked, setSiteUnlocked] = useState(false);
  const [game, setGame] = useState<GameState | null>(null);
  const [session, setSession] = useState<Session | null>(() => readSession());
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [rulesOpen, setRulesOpen] = useState(false);
  const [voiceLinesOpen, setVoiceLinesOpen] = useState(false);
  // Deep link: /?code=VEIL drops a friend straight onto a pre-filled join form.
  const [inviteCode] = useState(readInviteCode);
  // Mirrored so the long-lived socket listeners can read it without resubscribing.
  const siteUnlockedRef = useRef(false);

  const closeRules = useCallback(() => setRulesOpen(false), []);
  const closeVoiceLines = useCallback(() => setVoiceLinesOpen(false), []);

  useEffect(() => {
    siteUnlockedRef.current = siteUnlocked;
  }, [siteUnlocked]);

  useEffect(() => {
    void preloadVoiceLines().catch(() => {
      // Local voice lines are an optional enhancement. Storage failures must
      // never prevent the game from loading.
    });
  }, []);

  useEffect(() => {
    fetch(apiUrl("/api/config"))
      .then((response) => response.json())
      .then(({ sitePasswordRequired: required }) => {
        setSitePasswordRequired(Boolean(required));
        if (!required) {
          connect("");
        } else {
          const saved = localStorage.getItem(SITE_KEY);
          if (saved) connect(saved);
        }
      })
      .catch(() => {
        setSitePasswordRequired(false);
        connect("");
      });

    socket.on("connect", () => {
      setConnected(true);
      setSiteUnlocked(true);
      setError("");
      const stored = readSession();
      if (stored) {
        emitAck(socket, "room:join", {
          code: stored.code,
          name: stored.name,
          password: stored.roomPassword,
          playerToken: stored.playerToken
        }).then((ack) => {
          // Only surrender the seat when the chamber actually refused it. A
          // dropped socket is retryable and must not wipe the session.
          if (!ack.ok && !ack.retryable) {
            clearSession();
            setSession(null);
            setGame(null);
          }
        });
      }
    });
    socket.on("disconnect", () => setConnected(false));
    socket.on("connect_error", (reason: unknown) => {
      setConnected(false);
      const rejected = reason instanceof ConnectError && reason.auth;
      const message =
        reason instanceof Error ? reason.message : "The chamber could not be reached.";
      if (rejected) {
        // A genuine 401/403 is the only reason to forget the access phrase.
        localStorage.removeItem(SITE_KEY);
        setSiteUnlocked(false);
        setError(message);
        return;
      }
      // Network blip: stay where you are. The socket retries with backoff and
      // ConnectionBar already tells the player what is happening. Only speak up
      // while the player is still stuck outside the doors.
      if (!siteUnlockedRef.current) setError(message);
    });
    socket.on("room:state", (next: GameState) => setGame(next));

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
    // Socket is intentionally created once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(""), 3000);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const connect = (password: string) => {
    if (socket.connected) socket.disconnect();
    socket.auth = { sitePassword: password };
    if (password) localStorage.setItem(SITE_KEY, password);
    socket.connect();
  };

  const run = async (event: string, payload: unknown = {}) => {
    const ack = await emitAck(socket, event, payload);
    if (!ack.ok) {
      if (ack.error && STALE_DECISION_ERRORS.has(ack.error)) {
        setError("");
        return false;
      }
      setError(ack.error || "The chamber refused that move.");
      if (navigator.vibrate) navigator.vibrate([40, 30, 40]);
      return false;
    }
    setError("");
    if (navigator.vibrate) navigator.vibrate(18);
    return true;
  };

  const enterRoom = (next: Session) => {
    localStorage.setItem(SESSION_KEY, JSON.stringify(next));
    clearInviteCode();
    setSession(next);
  };

  const leave = () => {
    clearSession();
    setSession(null);
    setGame(null);
    socket.disconnect();
    socket.connect();
  };

  if (sitePasswordRequired === null) return <LoadingScreen />;
  if (sitePasswordRequired && !siteUnlocked) {
    return <GateScreen onUnlock={connect} error={error} />;
  }

  return (
    <main className="app-shell">
      <AnimatePresence mode="wait">
        {!session || !game ? (
          <HomeScreen
            socket={socket}
            connected={connected}
            error={error}
            inviteCode={inviteCode}
            onError={setError}
            onEnter={enterRoom}
            onRules={() => setRulesOpen(true)}
            onVoiceLines={() => setVoiceLinesOpen(true)}
          />
        ) : game.phase === "lobby" ? (
          <LobbyScreen
            game={game}
            session={session}
            connected={connected}
            onStart={() => run("game:start")}
            onRemove={(targetId) => run("lobby:remove", { targetId })}
            onLeave={leave}
            onToast={setToast}
            onRules={() => setRulesOpen(true)}
          />
        ) : (
          <GameScreen
            game={game}
            session={session}
            connected={connected}
            onRun={run}
            onLeave={leave}
            onRules={() => setRulesOpen(true)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {error && (
          <motion.button
            type="button"
            className="toast toast--error"
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            onClick={() => setError("")}
          >
            {error}
            <X size={14} />
          </motion.button>
        )}
        {toast && (
          <motion.div
            className="toast"
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
          >
            <Check size={15} /> {toast}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {rulesOpen && <RulesSheet onClose={closeRules} />}
        {voiceLinesOpen && <VoiceLinesSheet onClose={closeVoiceLines} onToast={setToast} />}
      </AnimatePresence>
    </main>
  );
}

function LoadingScreen() {
  return (
    <main className="app-shell app-shell--centered">
      <motion.div
        className="loading-mark"
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 5, ease: "linear" }}
      >
        V
      </motion.div>
    </main>
  );
}

function GateScreen({ onUnlock, error }: { onUnlock: (password: string) => void; error: string }) {
  const [password, setPassword] = useState("");
  return (
    <motion.main
      className="app-shell app-shell--centered gate-screen"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="gate-screen__content">
        <Wordmark />
        <form
          className="gate-form"
          onSubmit={(event) => {
            event.preventDefault();
            onUnlock(password);
          }}
        >
          <label htmlFor="outer-passphrase">The chamber is private</label>
          <div className="field-row">
            <input
              id="outer-passphrase"
              type="password"
              value={password}
              autoComplete="current-password"
              placeholder="Shared access phrase"
              onChange={(event) => setPassword(event.target.value)}
            />
            <button className="button button--primary button--square" type="submit" aria-label="Enter">
              <DoorOpen size={19} />
            </button>
          </div>
          {error && <small className="field-error">{error}</small>}
        </form>
      </div>
    </motion.main>
  );
}

function HomeScreen({
  socket,
  connected,
  error,
  inviteCode,
  onError,
  onEnter,
  onRules,
  onVoiceLines
}: {
  socket: GameSocket;
  connected: boolean;
  error: string;
  inviteCode: string;
  onError: (value: string) => void;
  onEnter: (session: Session) => void;
  onRules: () => void;
  onVoiceLines: () => void;
}) {
  const [mode, setMode] = useState<"create" | "join">(inviteCode ? "join" : "create");
  const [name, setName] = useState("");
  const [code, setCode] = useState(inviteCode);
  const [password, setPassword] = useState("");
  const [avatar, setAvatar] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [soloBusy, setSoloBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    const ack = await emitAck(socket, mode === "create" ? "room:create" : "room:join", {
      name,
      code,
      password,
      avatar
    });
    setBusy(false);
    if (!ack.ok || !ack.code || !ack.playerId || !ack.playerToken) {
      onError(ack.error || "Could not enter the chamber.");
      return;
    }
    onError("");
    onEnter({
      code: ack.code,
      playerId: ack.playerId,
      playerToken: ack.playerToken,
      name,
      roomPassword: password
    });
  };

  const startSolo = async () => {
    setSoloBusy(true);
    try {
      const soloPassword = `solo-${createId()}`;
      const soloName = name.trim() || "You";
      const ack = await emitAck(socket, "room:create", {
        name: soloName,
        password: soloPassword,
        bot: true,
        avatar
      });
      if (!ack.ok || !ack.code || !ack.playerId || !ack.playerToken) {
        onError(ack.error || "Could not start a solo game.");
        return;
      }
      onError("");
      onEnter({
        code: ack.code,
        playerId: ack.playerId,
        playerToken: ack.playerToken,
        name: soloName,
        roomPassword: soloPassword
      });
    } catch (error) {
      onError(error instanceof Error ? error.message : "Could not start a solo game.");
    } finally {
      setSoloBusy(false);
    }
  };

  return (
    <motion.section className="home-screen" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <header className="home-header">
        <div className="home-header__tools">
          <button className="icon-button" type="button" onClick={onRules} aria-label="Rules and information">
            <Info size={18} />
          </button>
          <button className="icon-button icon-button--voice" type="button" onClick={onVoiceLines} aria-label="Custom voice lines">
            <Mic2 size={18} />
          </button>
        </div>
        <ConnectionDot connected={connected} />
      </header>
      <div className="home-screen__hero">
        <Wordmark />
      </div>
      <form className="entry-card" onSubmit={submit}>
        <div className="segmented-control">
          <button type="button" className={mode === "create" ? "is-active" : ""} onClick={() => setMode("create")}>
            Open a room
          </button>
          <button type="button" className={mode === "join" ? "is-active" : ""} onClick={() => setMode("join")}>
            Join friends
          </button>
        </div>
        {inviteCode && mode === "join" && (
          <p className="entry-card__invite">
            <Send size={14} /> You were invited to room <b>{inviteCode}</b>
          </p>
        )}
        <label>
          <span>Your name</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="What should we call you?"
            maxLength={20}
            autoComplete="nickname"
          />
        </label>
        {mode === "join" && (
          <label>
            <span>Room code</span>
            <input
              className="code-input"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4))}
              placeholder="V E I L"
              maxLength={4}
              autoCapitalize="characters"
            />
          </label>
        )}
        <label>
          <span>Room passphrase</span>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            placeholder={mode === "create" ? "Choose one for your friends" : "Ask the host"}
            minLength={4}
            maxLength={80}
            autoComplete="off"
          />
        </label>
        <AvatarCapture name={name} value={avatar} onChange={setAvatar} />
        <button
          className="button button--primary button--full"
          type="submit"
          disabled={!connected || busy || soloBusy || !name.trim() || password.length < 4 || (mode === "join" && code.length !== 4)}
        >
          {busy ? "Opening the doors…" : mode === "create" ? "Open the chamber" : "Take your seat"}
        </button>
        <div className="solo-divider">
          <span>or</span>
        </div>
        <motion.button
          className="solo-play"
          type="button"
          disabled={!connected || busy || soloBusy}
          onClick={startSolo}
          whileTap={{ scale: 0.97 }}
        >
          <span className="solo-play__bot">
            <Bot size={24} strokeWidth={2.2} />
          </span>
          <span className="solo-play__copy">
            <b>{soloBusy ? "WINDING UP MOXIE…" : "PLAY SOLO"}</b>
            <small>Instant 6-player game</small>
          </span>
          <span className="solo-play__go">→</span>
        </motion.button>
        {error && <small className="field-error">{error}</small>}
      </form>
          <p className="home-screen__fineprint">5 solo bots · 2–6 friends · No account</p>
    </motion.section>
  );
}

function LobbyScreen({
  game,
  session,
  connected,
  onStart,
  onRemove,
  onLeave,
  onToast,
  onRules
}: {
  game: GameState;
  session: Session;
  connected: boolean;
  onStart: () => void;
  onRemove: (targetId: string) => void;
  onLeave: () => void;
  onToast: (message: string) => void;
  onRules: () => void;
}) {
  const meIsHost = game.hostId === session.playerId;
  const [passShown, setPassShown] = useState(false);
  const passphrase = session.roomPassword || "";
  // Solo rooms mint a throwaway passphrase nobody ever needs to read.
  const showPassphrase = passphrase.length > 0 && !passphrase.startsWith("solo-");
  const link = inviteLink(game.code);

  const share = async () => {
    const text = `Join my VEIL room ${game.code}: ${link}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "VEIL", text, url: link });
        return;
      }
    } catch {
      // Share sheet dismissed — fall through to the clipboard.
    }
    onToast((await copyText(link)) ? "Invite link copied" : link);
  };

  const copyPassphrase = async () => {
    onToast((await copyText(passphrase)) ? "Passphrase copied" : "Could not copy — tap the eye");
  };

  return (
    <motion.section
      className={showPassphrase ? "lobby-screen lobby-screen--pass" : "lobby-screen"}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <header className="screen-header">
        <button className="icon-button" type="button" onClick={onLeave} aria-label="Leave room">
          <ChevronLeft size={20} />
        </button>
        <Wordmark compact />
        <button className="icon-button" type="button" onClick={onRules} aria-label="Rules">
          <BookOpen size={18} />
        </button>
      </header>
      <div className="lobby-code">
        <span className="eyebrow">Your private room</span>
        <button
          type="button"
          onClick={async () => {
            onToast((await copyText(game.code)) ? "Room code copied" : `Room code ${game.code}`);
          }}
        >
          {game.code}
          <Clipboard size={16} />
        </button>
        <small>Tap the code to copy it</small>
      </div>

      {showPassphrase && (
        <div className="lobby-pass">
          <span className="lobby-pass__label">
            <KeyRound size={13} /> Room passphrase
          </span>
          <div className="lobby-pass__row">
            <button
              className="lobby-pass__value"
              type="button"
              onClick={copyPassphrase}
              aria-label="Copy room passphrase"
            >
              <span className={passShown ? "" : "lobby-pass__masked"}>
                {passShown ? passphrase : "•".repeat(Math.min(14, passphrase.length))}
              </span>
              <Clipboard size={15} />
            </button>
            <button
              className="icon-button"
              type="button"
              onClick={() => setPassShown((shown) => !shown)}
              aria-label={passShown ? "Hide room passphrase" : "Show room passphrase"}
            >
              {passShown ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <small>Send this to your friends separately from the link</small>
        </div>
      )}
      <div className="seat-list">
        <div className="seat-list__heading">
          <span>
            <Users size={16} /> The chamber
          </span>
          <b>{game.players.length}/6</b>
        </div>
        {game.players.map((player, index) => (
          <motion.div className="seat-row" layout key={player.id}>
            <span className="seat-row__number">{String(index + 1).padStart(2, "0")}</span>
            <span className="seat-row__avatar">
              {player.avatar ? (
                <img src={player.avatar} alt="" />
              ) : (
                player.name.slice(0, 1).toUpperCase()
              )}
            </span>
            <span className="seat-row__name">
              {player.name}
              {player.id === session.playerId && <small>You</small>}
            </span>
            {player.id === game.hostId && <span className="seat-row__host">HOST</span>}
            {!player.connected && <WifiOff size={14} />}
            {meIsHost && player.id !== session.playerId && (
              <button className="icon-button icon-button--small" type="button" onClick={() => onRemove(player.id)}>
                <UserMinus size={14} />
              </button>
            )}
          </motion.div>
        ))}
        {Array.from({ length: Math.max(0, 2 - game.players.length) }, (_, index) => (
          <div className="seat-row seat-row--empty" key={`empty-${index}`}>
            <span className="seat-row__number">—</span>
            <span>Waiting for a rival…</span>
          </div>
        ))}
      </div>
      <div className="lobby-actions">
        <button className="button button--ghost" type="button" onClick={share}>
          <Share2 size={17} /> Invite friends
        </button>
        {meIsHost ? (
          <button className="button button--primary" type="button" disabled={game.players.length < 2} onClick={onStart}>
            <Sparkles size={17} /> Seal the chamber
          </button>
        ) : (
          <div className="host-waiting">
            <motion.span animate={{ opacity: [0.25, 1, 0.25] }} transition={{ repeat: Infinity, duration: 1.8 }} />
            Waiting for the host
          </div>
        )}
      </div>
      <ConnectionBar connected={connected} />
    </motion.section>
  );
}

function GameScreen({
  game,
  session,
  connected,
  onRun,
  onLeave,
  onRules
}: {
  game: GameState;
  session: Session;
  connected: boolean;
  onRun: (event: string, payload?: unknown) => Promise<boolean>;
  onLeave: () => void;
  onRules: () => void;
}) {
  const [targetAction, setTargetAction] = useState<ActionType | null>(null);
  const me = game.players.find((player) => player.id === session.playerId)!;
  const winner = game.players.find((player) => player.id === game.winnerId);
  const solo = game.players.some((player) => player.bot);
  const botCount = game.players.filter((player) => player.bot).length;
  const isMyTurn = game.phase === "turn" && game.activePlayerId === me.id && me.alive;
  const targetActionData = targetAction ? ACTIONS[targetAction] : null;
  const TargetIcon = targetActionData?.icon;
  // The single most recent declaration, for the table's current-event line.
  // One entry, never a list — there is no play history in VEIL.
  const lastAction = game.lastPlay;
  const lastActionData = lastAction ? ACTIONS[lastAction.actionType] : null;
  const lastActionActor = game.players.find((player) => player.id === lastAction?.playerId);
  const lastActionTarget = game.players.find((player) => player.id === lastAction?.targetId);
  const activePlayer = game.players.find((player) => player.id === game.activePlayerId);
  const activePlayerIsYou = activePlayer?.name.toLowerCase() === "you";
  const currentEventLabel =
    lastAction && lastActionData
      ? describeTablePlay(
          lastActionData.name,
          lastActionActor?.name ?? "A player",
          lastActionTarget?.name
        )
      : `${activePlayer?.name ?? "The chamber"} ${
          activePlayerIsYou ? "begin" : "begins"
        }`;
  const currentEventDetail = isMyTurn
    ? "You decide next"
    : activePlayer
      ? `${activePlayer.name} ${activePlayerIsYou ? "are" : "is"} deciding`
      : "The chamber is settling";
  const interferenceActive = ["response_window", "claim_window"].includes(game.phase);
  const pendingPlayer = game.players.find(
    (player) =>
      player.id === game.pendingLoss?.playerId ||
      player.id === game.exchangeChoices?.playerId
  );
  const pendingPlayerIsYou = pendingPlayer?.name.toLowerCase() === "you";
  const resolutionLabel =
    game.phase === "lose_influence" && pendingPlayer
      ? `${pendingPlayer.name} must reveal Influence`
      : game.phase === "exchange" && pendingPlayer
        ? `${pendingPlayer.name} ${pendingPlayerIsYou ? "choose" : "chooses"} new Influence`
        : null;

  const declareAction = (type: ActionType, targetId?: string) => {
    playVoiceLine(voiceLineForAction(type));
    return onRun("game:action", targetId ? { type, targetId } : { type });
  };

  const declareBlock = (role: Role) => {
    const voiceLine = voiceLineForBlock(game.action?.type, role);
    if (voiceLine) playVoiceLine(voiceLine);
    return onRun("game:block", { role });
  };

  const declareChallenge = () => {
    playVoiceLine("response:challenge");
    return onRun("game:challenge");
  };

  useEffect(() => {
    if (!isMyTurn) setTargetAction(null);
  }, [isMyTurn]);

  return (
    <motion.section className="game-screen" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <header className="game-header">
        <button className="icon-button" type="button" onClick={onRules} aria-label="Open role guide">
          <BookOpen size={17} />
        </button>
        <button
          className="game-header__code"
          type="button"
          onClick={() => {
            if (!solo) navigator.clipboard.writeText(game.code);
          }}
        >
          {solo ? `SOLO · ${botCount} BOTS` : `ROOM ${game.code}`}
        </button>
        <button className="icon-button" type="button" onClick={onLeave} aria-label="Leave game">
          <LogOut size={17} />
        </button>
      </header>

      <section className={targetAction ? "table-stage table-stage--targeting" : "table-stage"}>
        <PlayerRail
          players={game.players}
          meId={me.id}
          activePlayerId={game.activePlayerId}
          targeting={Boolean(targetAction)}
          onTarget={(targetId) => {
            if (!targetAction) return;
            const type = targetAction;
            setTargetAction(null);
            declareAction(type, targetId);
          }}
        />

        {/* Doubles as the bank: coins taken from or paid to the table fly here. */}
        <section
          ref={anchorRef(TABLE_CENTER)}
          className={targetAction ? "court-center court-center--targeting" : "court-center"}
        >
          <AnimatePresence mode="wait">
            {targetAction && targetActionData && TargetIcon ? (
              <motion.div
                className="target-stage"
                key={targetAction}
                initial={{ scale: 0.78, opacity: 0, rotate: -3 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                exit={{ scale: 0.84, opacity: 0 }}
              >
                <motion.span
                  className="target-stage__arrow"
                  animate={{ scale: [1, 1.06, 1] }}
                  transition={{ repeat: Infinity, duration: 1.1 }}
                >
                  Choose a seat
                </motion.span>
                <span className="target-stage__icon">
                  <TargetIcon size={29} strokeWidth={1.9} />
                </span>
                <span className="eyebrow">
                  {targetActionData.role ? `Claim ${ROLES[targetActionData.role].name}` : "Direct action"}
                </span>
                <h1>{targetActionData.name}</h1>
                <p>{targetActionData.detail}</p>
              </motion.div>
            ) : interferenceActive ? (
              <InterferencePanel
                game={game}
                me={me}
                onPass={() => onRun("game:pass")}
                onChallenge={declareChallenge}
                onBlock={declareBlock}
              />
            ) : resolutionLabel ? (
              <motion.div
                className="resolution-event"
                key={resolutionLabel}
                initial={false}
                animate={{ opacity: 1, y: 0 }}
              >
                <span>{resolutionLabel}</span>
              </motion.div>
            ) : (
              <motion.div
                className="table-event"
                key={lastAction?.id ?? "no-actions"}
                initial={false}
                animate={{ opacity: 1 }}
              >
                <h2>{currentEventLabel}</h2>
                <p>{currentEventDetail}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
      </section>

      <DecisionPanel
        game={game}
        me={me}
        onLose={(cardId) => onRun("game:lose", { cardId })}
        onExchange={(keepIds) => onRun("game:exchange", { keepIds })}
      />

      {game.phase === "game_over" && (
        <VictoryStage
          game={game}
          me={me}
          winner={winner}
          onRematch={() => onRun("game:restart")}
          onLeave={onLeave}
        />
      )}

      <MyHand
        me={me}
        disabled={!isMyTurn}
        targeting={targetAction}
        onTarget={setTargetAction}
        onCancelTarget={() => setTargetAction(null)}
        onDeclare={(type: ActionType) => onRun("game:action", { type })}
      />

      <ActionDock
        me={me}
        disabled={!isMyTurn}
        targeting={targetAction}
        onTarget={setTargetAction}
        onCancelTarget={() => setTargetAction(null)}
        onDeclare={(type: ActionType) => declareAction(type)}
      />

      {/* Sits on `.game-screen`, never inside `.table-stage` — that one clips.
          The layer only paints; it never gates or delays a snapshot. */}
      <TableFx game={game} meId={me.id} />

      <ConnectionBar connected={connected} />
    </motion.section>
  );
}

/**
 * The climax. An ink wipe, the panel springing in, confetti, then every hand at
 * the table flipping face up (the server unmasks all roles at `game_over`), and
 * a one-tap rematch that keeps the room, the seats and the passphrase alive.
 */
function VictoryStage({
  game,
  me,
  winner,
  onRematch,
  onLeave
}: {
  game: GameState;
  me: Player;
  winner: Player | undefined;
  onRematch: () => Promise<boolean>;
  onLeave: () => void;
}) {
  const reduced = useReducedMotion() ?? false;
  const [rematchBusy, setRematchBusy] = useState(false);
  const iWon = Boolean(winner) && winner?.id === me.id;
  const isHost = game.hostId === me.id;

  const confetti = useMemo(
    () =>
      Array.from({ length: 18 }, (_, index) => ({
        id: index,
        color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
        x: rand(-170, 170),
        y: rand(120, 540),
        rotate: rand(-540, 540),
        duration: rand(1.1, 1.9),
        delay: rand(0, 0.24)
      })),
    []
  );

  useEffect(() => {
    if (navigator.vibrate) navigator.vibrate(iWon ? [40, 60, 40, 60, 150] : [30, 70, 90]);
  }, [iWon]);

  const panelDelay = reduced ? 0 : 0.26;
  const revealBase = panelDelay + (reduced ? 0.08 : 0.34);
  const revealStagger = reduced ? 0.04 : 0.09;

  const callRematch = async () => {
    if (rematchBusy) return;
    setRematchBusy(true);
    const ok = await onRematch();
    // On success the fresh state unmounts this panel; on failure, let them retry.
    if (!ok) setRematchBusy(false);
  };

  return (
    <>
      {!reduced && (
        <motion.div
          className="victory-wipe"
          aria-hidden="true"
          initial={{ scaleY: 0 }}
          animate={{ scaleY: [0, 1, 0] }}
          transition={{ duration: 0.44, times: [0, 0.5, 1], ease: "easeInOut" }}
        />
      )}

      {!reduced && (
        <span className="victory-confetti" aria-hidden="true">
          {confetti.map((chip) => (
            <motion.i
              key={chip.id}
              style={{ background: chip.color }}
              initial={{ x: 0, y: 0, rotate: 0, opacity: 0 }}
              animate={{ x: chip.x, y: chip.y, rotate: chip.rotate, opacity: [0, 1, 1, 0] }}
              transition={{
                duration: chip.duration,
                delay: panelDelay + 0.14 + chip.delay,
                ease: "easeOut"
              }}
            />
          ))}
        </span>
      )}

      <motion.div
        className="victory-panel victory-panel--reveal"
        initial={reduced ? { opacity: 0 } : { scale: 0.5, rotate: -6, opacity: 0 }}
        animate={{ scale: 1, rotate: 0, opacity: 1 }}
        transition={
          reduced
            ? { duration: 0.22, delay: panelDelay }
            : { type: "spring", stiffness: 260, damping: 16, delay: panelDelay }
        }
      >
        <span className="victory-panel__sigil">V</span>
        <span className="eyebrow">The chamber yields</span>
        <h2>{winner?.name ?? "Nobody"}</h2>
        <p>{iWon ? "You kept the final secret." : "The final secret was theirs."}</p>

        <div className="victory-reveal">
          <span className="victory-reveal__title">Every hand, unveiled</span>
          <ul className="victory-reveal__list">
            {game.players.map((player, index) => (
              <li
                className={
                  player.id === winner?.id
                    ? "victory-reveal__row victory-reveal__row--winner"
                    : "victory-reveal__row"
                }
                key={player.id}
              >
                <span className="victory-reveal__who">
                  {player.name}
                  {player.id === me.id && <small>YOU</small>}
                </span>
                <span className="victory-reveal__cards">
                  {player.influence.map((card, cardIndex) => (
                    <VictoryCard
                      key={card.id}
                      card={card}
                      reduced={reduced}
                      delay={revealBase + index * revealStagger + cardIndex * 0.05}
                    />
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="victory-panel__actions">
          {isHost ? (
            <button
              className="button button--primary button--full"
              type="button"
              onClick={callRematch}
              disabled={rematchBusy}
            >
              <RotateCcw size={17} /> {rematchBusy ? "Dealing again…" : "Rematch"}
            </button>
          ) : (
            <div className="host-waiting">
              <motion.span animate={{ opacity: [0.25, 1, 0.25] }} transition={{ repeat: Infinity, duration: 1.8 }} />
              Waiting for the host to deal again
            </div>
          )}
          <button className="button button--ghost button--full" type="button" onClick={onLeave}>
            <DoorOpen size={16} /> Return to the doors
          </button>
        </div>
      </motion.div>
    </>
  );
}

function VictoryCard({
  card,
  delay,
  reduced
}: {
  card: Influence;
  delay: number;
  reduced: boolean;
}) {
  const role = card.role ? ROLES[card.role] : null;
  return (
    <motion.span
      className={card.revealed ? "victory-card victory-card--lost" : "victory-card"}
      style={{ "--role-color": role?.color ?? "#ad8e5f" } as React.CSSProperties}
      initial={reduced ? { opacity: 0 } : { rotateY: 180, opacity: 0, scale: 0.86 }}
      animate={{ rotateY: 0, opacity: 1, scale: 1 }}
      transition={{ delay, duration: reduced ? 0.18 : 0.46, ease: [0.16, 1, 0.3, 1] }}
    >
      {role && <img src={role.image} alt={role.name} title={role.name} draggable={false} />}
    </motion.span>
  );
}

function rand(min: number, max: number) {
  return min + Math.random() * (max - min);
}

function describeTablePlay(actionName: string, actorName: string, targetName?: string) {
  const isYou = actorName.toLowerCase() === "you";
  switch (actionName) {
    case "Income":
      return `${actorName} ${isYou ? "take" : "takes"} Income`;
    case "Foreign Aid":
      return `${actorName} ${isYou ? "take" : "takes"} Foreign Aid`;
    case "Tax":
      return `${actorName} ${isYou ? "take" : "takes"} Tax`;
    case "Exchange":
      return `${actorName} ${isYou ? "exchange" : "exchanges"} Influence`;
    case "Steal":
      return `${actorName} ${isYou ? "steal" : "steals"} from ${targetName ?? "a rival"}`;
    case "Assassinate":
      return `${actorName} ${isYou ? "move" : "moves"} against ${targetName ?? "a rival"}`;
    case "Coup":
      return `${actorName} ${isYou ? "coup" : "coups"} ${targetName ?? "a rival"}`;
    default:
      return `${actorName} ${isYou ? "make" : "makes"} a move`;
  }
}

function RulesSheet({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      className="sheet-backdrop sheet-backdrop--high"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onPointerDown={(event) => {
        // Backdrop only — a drag that began inside the sheet must not close it.
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.section
        className="rules-sheet"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
      >
        <header>
          <div>
            <span className="eyebrow">Private field guide</span>
            <h2>Know the Court</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose}>
            <X size={18} />
          </button>
        </header>
        <div className="rules-sheet__scroll">
          <section className="quick-rule">
            <p>
              Bluff. Block. Challenge. Lose both Influence cards and you are out. Last player standing wins.
            </p>
            <div className="quick-rule__actions">
              <span><b>Income</b> +1, safe</span>
              <span><b>Foreign Aid</b> +2, Duke blocks</span>
              <span><b>Coup</b> costs 7, unblockable</span>
            </div>
          </section>
          <section className="role-guide">
            {(Object.keys(ROLES) as Role[]).map((role) => (
              <div className="role-guide__row" key={role} style={{ "--role-color": ROLES[role].color } as React.CSSProperties}>
                <img className="role-guide__monster" src={ROLES[role].image} alt="" />
                <span>
                  <b>{ROLES[role].name}</b>
                  <small>{ROLES[role].ability}</small>
                </span>
                <em>{ROLES[role].blocks}</em>
              </div>
            ))}
          </section>
          <section className="timing-guide">
            <h3>Challenge timing</h3>
            <p>
              Most role moves happen, then pause for a challenge. A successful challenge rolls the move back.
            </p>
            <p>
              <b>Ambassador is different:</b> everyone answers before any deck cards appear.
            </p>
          </section>
          <section className="legal-note">
            <Info size={16} />
            <p>
              VEIL uses familiar role vocabulary with original writing, code, visual art, and presentation.
              It is not affiliated with or endorsed by any tabletop publisher.
            </p>
          </section>
        </div>
      </motion.section>
    </motion.div>
  );
}

function ConnectionDot({ connected }: { connected: boolean }) {
  return (
    <span className={connected ? "connection-dot connection-dot--on" : "connection-dot"}>
      {connected ? <Wifi size={14} /> : <WifiOff size={14} />}
      {connected ? "Live" : "Offline"}
    </span>
  );
}

function ConnectionBar({ connected }: { connected: boolean }) {
  if (connected) return null;
  return (
    <motion.div className="connection-bar" initial={{ y: 20 }} animate={{ y: 0 }}>
      <WifiOff size={14} /> Reconnecting to the chamber…
    </motion.div>
  );
}

function readSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

function clearSession() {
  localStorage.removeItem(SESSION_KEY);
}

/** `?code=VEIL` on the landing URL, sanitised to the same shape as the input. */
function readInviteCode(): string {
  try {
    const raw = new URLSearchParams(window.location.search).get("code") ?? "";
    return raw.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4);
  } catch {
    return "";
  }
}

/** Drop the invite param once it has been consumed, so a refresh is not sticky. */
function clearInviteCode() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("code")) return;
    url.searchParams.delete("code");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  } catch {
    // A history-less environment simply keeps the param.
  }
}

function inviteLink(code: string): string {
  try {
    const url = new URL(window.location.href);
    url.hash = "";
    url.search = "";
    url.searchParams.set("code", code);
    return url.toString();
  } catch {
    return code;
  }
}

async function copyText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Insecure context or a denied permission — the caller falls back to text.
  }
  return false;
}

function emitAck(socket: GameSocket, event: string, payload: unknown): Promise<AckResult> {
  return new Promise((resolve) => {
    const timeout = window.setTimeout(
      () => resolve({ ok: false, error: "The chamber did not answer.", retryable: true }),
      8000
    );
    socket.emit(event, payload, (ack: AckResult) => {
      window.clearTimeout(timeout);
      resolve(ack);
    });
  });
}

export default App;
